import React, { useState, useEffect } from 'react';
import { Resource } from '../../types';
import Modal from '../../components/Modal';
import { resourceApi } from '../../services/api';
import { PlusCircleIcon, PencilIcon, TrashIcon, DocumentTextIcon, VideoCameraIcon, ArchiveBoxIcon, AcademicCapIcon } from '../../components/icons';

const ManageResourcesPage: React.FC = () => {
  const [resources, setResources] = useState<Resource[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentResource, setCurrentResource] = useState<Partial<Resource> | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchResources();
  }, []);

  const fetchResources = async () => {
    try {
      setLoading(true);
      const response = await resourceApi.getAllResources();
      setResources(response.data.data.resources);
      setError(null);
    } catch (err) {
      setError('Failed to load resources. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const getIconForType = (type?: 'PDF' | 'DOC' | 'ZIP' | 'VIDEO') => {
    const iconProps = { className: 'w-5 h-5 mr-2 text-primary-light' };
    switch (type) {
      case 'PDF': return <DocumentTextIcon {...iconProps} />;
      case 'DOC': return <DocumentTextIcon {...iconProps} />; // Or a specific DOC icon
      case 'ZIP': return <ArchiveBoxIcon {...iconProps} />;
      case 'VIDEO': return <VideoCameraIcon {...iconProps} />;
      default: return <DocumentTextIcon {...iconProps} />;
    }
  };

  const openModalForNew = () => {
    setIsEditing(false);
    setCurrentResource({ 
      title: '', 
      description: '', 
      fileUrl: '', 
      fileType: 'PDF', 
      category: 'General',
      type: 'document'
    });
    setIsModalOpen(true);
  };

  const openModalForEdit = (resource: Resource) => {
    setIsEditing(true);
    setCurrentResource({ ...resource });
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setCurrentResource(null);
  };

  const handleDelete = async (resourceId: string) => {
    if (window.confirm('Are you sure you want to delete this resource?')) {
      try {
        await resourceApi.deleteResource(resourceId);
        await fetchResources(); // Refresh the list
      } catch (err) {
        alert('Failed to delete resource. Please try again.');
      }
    }
  };

  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    if (!currentResource) return;
    const { name, value } = e.target;
    setCurrentResource({ ...currentResource, [name]: value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentResource) return;

    try {
      if (isEditing && currentResource.id) {
        await resourceApi.updateResource(currentResource.id, currentResource);
      } else {
        await resourceApi.createResource(currentResource);
      }
      await fetchResources(); // Refresh the list
      closeModal();
    } catch (err) {
      alert('Failed to save resource. Please try again.');
    }
  };
  
  const resourceCategories = ['Web Development', 'Data Science', 'Design', 'Cloud Computing', 'General', 'Forms'];
  const fileTypes: Resource['fileType'][] = ['PDF', 'DOC', 'ZIP', 'VIDEO'];

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center text-red-500 py-8">
        <p>{error}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-semibold text-white">Manage Resources</h2>
        <button
          onClick={openModalForNew}
          className="flex items-center bg-primary hover:bg-primary-dark text-white font-medium py-2 px-4 rounded-lg transition-colors"
        >
          <PlusCircleIcon className="w-5 h-5 mr-2" />
          Add New Resource
        </button>
      </div>

      <div className="bg-neutral-dark shadow-xl rounded-lg overflow-x-auto">
        <table className="w-full text-sm text-left text-neutral-300">
          <thead className="text-xs text-neutral-100 uppercase bg-neutral-darker">
            <tr>
              <th scope="col" className="px-6 py-3">Title</th>
              <th scope="col" className="px-6 py-3">Category</th>
              <th scope="col" className="px-6 py-3">Type</th>
              <th scope="col" className="px-6 py-3">File Type</th>
              <th scope="col" className="px-6 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {resources.map((resource) => (
              <tr key={resource.id} className="bg-neutral-dark border-b border-neutral-700 hover:bg-neutral-dark/70">
                <td className="px-6 py-4 font-medium text-white whitespace-nowrap flex items-center">
                    {getIconForType(resource.fileType)} {resource.title}
                </td>
                <td className="px-6 py-4">{resource.category}</td>
                <td className="px-6 py-4">{resource.type}</td>
                <td className="px-6 py-4">{resource.fileType}</td>
                <td className="px-6 py-4 text-right space-x-2">
                  <button onClick={() => openModalForEdit(resource)} className="text-primary-light hover:text-primary p-1" title="Edit">
                    <PencilIcon className="w-5 h-5" />
                  </button>
                  <button onClick={() => handleDelete(resource.id)} className="text-red-500 hover:text-red-400 p-1" title="Delete">
                    <TrashIcon className="w-5 h-5" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {isModalOpen && currentResource && (
        <Modal isOpen={isModalOpen} onClose={closeModal} title={isEditing ? 'Edit Resource' : 'Add New Resource'}>
          <form onSubmit={handleSubmit} className="space-y-4 text-neutral-light">
            <div>
              <label htmlFor="title" className="block text-sm font-medium mb-1">Title</label>
              <input type="text" name="title" id="title" value={currentResource.title || ''} onChange={handleFormChange} required className="w-full bg-neutral-darker p-2 rounded border border-neutral-600 focus:ring-primary focus:border-primary"/>
            </div>
            <div>
              <label htmlFor="description" className="block text-sm font-medium mb-1">Description</label>
              <textarea name="description" id="description" value={currentResource.description || ''} onChange={handleFormChange} rows={3} className="w-full bg-neutral-darker p-2 rounded border border-neutral-600 focus:ring-primary focus:border-primary"></textarea>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label htmlFor="category" className="block text-sm font-medium mb-1">Category</label>
                <select name="category" id="category" value={currentResource.category || ''} onChange={handleFormChange} required className="w-full bg-neutral-darker p-2 rounded border border-neutral-600 focus:ring-primary focus:border-primary">
                  {resourceCategories.map(cat => <option key={cat} value={cat}>{cat}</option>)}
                </select>
              </div>
              <div>
                <label htmlFor="fileType" className="block text-sm font-medium mb-1">File Type</label>
                <select name="fileType" id="fileType" value={currentResource.fileType || 'PDF'} onChange={handleFormChange} required className="w-full bg-neutral-darker p-2 rounded border border-neutral-600 focus:ring-primary focus:border-primary">
                  {fileTypes.map(type => <option key={type} value={type}>{type}</option>)}
                </select>
              </div>
              <div>
                <label htmlFor="type" className="block text-sm font-medium mb-1">Type</label>
                <select name="type" id="type" value={currentResource.type || 'document'} onChange={handleFormChange} required className="w-full bg-neutral-darker p-2 rounded border border-neutral-600 focus:ring-primary focus:border-primary">
                  <option value="document">Document</option>
                  <option value="video">Video</option>
                </select>
              </div>
            </div>
            <div>
              <label htmlFor="fileUrl" className="block text-sm font-medium mb-1">File URL</label>
              <input type="text" name="fileUrl" id="fileUrl" value={currentResource.fileUrl || ''} onChange={handleFormChange} required className="w-full bg-neutral-darker p-2 rounded border border-neutral-600 focus:ring-primary focus:border-primary"/>
            </div>
            <p className="text-xs text-neutral-500">Note: File upload functionality requires backend implementation. For now, please provide a direct URL to the resource.</p>
            <div className="flex justify-end space-x-3 pt-4">
              <button type="button" onClick={closeModal} className="px-4 py-2 rounded bg-neutral-600 hover:bg-neutral-500 transition-colors">Cancel</button>
              <button type="submit" className="px-4 py-2 rounded bg-primary hover:bg-primary-dark text-white transition-colors">{isEditing ? 'Save Changes' : 'Add Resource'}</button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
};

export default ManageResourcesPage;